import React from "react";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from "currency-symbol-map";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/liveOps/casbacks/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Tooltip from "components/Tooltip";
import styles from "./CashbackGamesConfigTab.module.scss";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  activeCurrency: string;
  setActiveCurrency: (e: string) => void;
  isReadOnly?: boolean;
  isLoading?: boolean;
}

const CashbackGamesConfigTab: React.FC<Props> = ({
  fieldErrors,
  clearFieldError,
  activeCurrency,
  setActiveCurrency,
  isReadOnly = false,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currenciesList = useAppSelector((store) => store.settings.currencies);
  const payoutCurrencies = useAppSelector(
    (store) => store.settings.payoutCurrencies,
  );

  const payload = useAppSelector((store) => store.cashbacks.payload);
  const currentItem = payload?.configs?.filter(
    (el) => el?.currency === activeCurrency,
  )[0];

  const handleDataConfig = (key: string, value: string | number) => {
    if (isReadOnly) return;
    clearFieldError(key);
    if (currentItem && payload?.configs) {
      // @ts-ignore
      dispatch(
        handleUpdateFields({
          ...payload,
          configs: [
            ...payload?.configs?.filter(
              (el) => el?.currency !== activeCurrency,
            ),
            {
              ...currentItem,
              currency: activeCurrency,
              [key]: value,
            },
          ],
        }),
      );
    } else {
      const config = payload?.configs
        ? payload?.configs?.filter((el) => el?.currency !== activeCurrency)
        : [];
      dispatch(
        // @ts-ignore
        handleUpdateFields({
          ...payload,
          configs: [
            // @ts-ignore
            ...config,
            // @ts-ignore
            {
              currency: activeCurrency,
              [key]: value,
            },
          ],
        }),
      );
    }
  };

  const payoutValue = currentItem?.payoutCurrency || "";
  const payoutSelectionInvalid =
    payoutValue !== "" && !payoutCurrencies.includes(payoutValue);

  return (
    <div className="cashback-games-config">
      <div className={styles.currencyHeader}>
        <div className={styles.currencyTitle}>
          {t("cashbackCalculationCurrency")}
          <Tooltip text={t("cashbackCalculationCurrencyTooltip")} />
        </div>
        <div className={styles.currencyHint}>
          {t("cashbackCalculationCurrencyHint")}
        </div>
      </div>
      <div className="game-modal-tabs bonus-currencies-tabs small">
        {currenciesList?.map((el) => (
          <div
            className={`tab-item ${activeCurrency === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setActiveCurrency(el);
            }}
          >
            <div className="currency-tab-item">
              <div className="currency">{getSymbolFromCurrency(el)}</div> {el}
            </div>
          </div>
        ))}
      </div>
      {isLoading ? (
        <div role="status" aria-label={t("loading")}>
          <ModalTabSkeleton />
        </div>
      ) : (
        <div className="bonus-inner-form small">
          <div className="item">
            <div className="row row-3">
              <InnerInput
                hasError={Boolean(fieldErrors?.["min"])}
                value={currentItem?.min ?? ""}
                placeholder={t("min")}
                label={t("min")}
                tooltipText={t("cashbackMinTooltip")}
                tooltipPosition="top-left"
                type="number"
                id="config-min"
                disabled={isReadOnly}
                onChange={(e) => handleDataConfig("min", e)}
              />
              <InnerInput
                hasError={Boolean(fieldErrors?.["max"])}
                value={currentItem?.max ?? ""}
                placeholder={t("max")}
                label={t("max")}
                tooltipText={t("cashbackMaxTooltip")}
                tooltipPosition="top-left"
                type="number"
                id="config-max"
                disabled={isReadOnly}
                onChange={(e) => handleDataConfig("max", e)}
              />
              <InnerInput
                hasError={Boolean(fieldErrors?.["percentage"])}
                value={currentItem?.percentage ?? ""}
                placeholder={t("cashbackPercentage")}
                label={t("cashbackPercentage")}
                tooltipText={t("cashbackPercentageTooltip")}
                tooltipPosition="top-left"
                type="number"
                step="0.0001"
                id="config-percentage"
                disabled={isReadOnly}
                onChange={(e) => handleDataConfig("percentage", e)}
              />
              <DefaultDropdown
                label={t("cashbackPayoutCurrency")}
                value={payoutValue}
                selectedLabel={payoutValue || undefined}
                placeholder={t("chooseCurrency")}
                hasError={
                  Boolean(fieldErrors?.["payoutCurrency"]) ||
                  payoutSelectionInvalid
                }
                options={payoutCurrencies.map((currency) => ({
                  value: currency,
                  label: currency,
                }))}
                disabled={isReadOnly}
                onSelect={(value) => handleDataConfig("payoutCurrency", value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashbackGamesConfigTab;
